import { cn } from '@/lib/utils';
import type { PillTone } from './tone';

/**
 * Status pill with a leading dot — the design prototype's `.pill` (mono
 * uppercase, soft status background). One component for every verdict-like
 * label in the report: channel verdicts, data-quality states, macro risk
 * levels, scenario viability.
 *
 * `tone` maps prototype status keys onto the app's semantic tokens, so the
 * pill holds up in dark mode without per-usage color plumbing.
 */
const TONES: Record<PillTone, string> = {
  ok: 'bg-success/10 text-success',
  warn: 'bg-warning/10 text-warning',
  crit: 'bg-destructive/10 text-destructive',
  info: 'bg-info/10 text-info',
  neutral: 'bg-muted text-muted-foreground',
};

export default function StatusPill({
  tone,
  children,
  className,
  dot = true,
}: {
  tone: PillTone;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] font-mono text-[10px] font-semibold uppercase tracking-[0.06em]',
        TONES[tone],
        className,
      )}
    >
      {dot && <i className="size-1.5 flex-none rounded-full bg-current" />}
      {children}
    </span>
  );
}
